import React, { useCallback, useEffect, useState } from "react";
import {
  Alert,
  Button,
  Card,
  InputNumber,
  Popconfirm,
  Space,
  Tag,
  Typography,
  message
} from "antd";
import { useTranslation } from "react-i18next";
import Amount from "../shared/Amount";
import { formatDate } from "../shared/money";
import ResponsiveTable from "../../../../design-system/ResponsiveTable";
import { errorText } from "../api";
import * as api from "./api";

/**
 * FA-4: Einzel-/Pauschalwertberichtigung at the year end (Bilanzierer). Shows the proposal
 * from the aged open items, lets the user change the percent per item and books the
 * difference on 31.12. (reversed on 01.01. of the next year). Hidden for EÜR tenants.
 */
const AllowanceSection = ({ year, onBooked }) => {
  const { t } = useTranslation("accounting");
  const [data, setData] = useState(null);
  const [overrides, setOverrides] = useState({});
  const [loading, setLoading] = useState(false);
  const [booking, setBooking] = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const res = await api.getAllowance(year);
      setData(res.data);
      setOverrides({});
    } catch (error) {
      message.error(errorText(error));
    } finally {
      setLoading(false);
    }
  }, [year]);

  useEffect(() => {
    load();
  }, [load]);

  if (!data || !data.applicable) return null;

  const items = (Array.isArray(data.items) ? data.items : []).map(i => {
    const percent = overrides[i.item] ?? i.percent;
    return { ...i, percent, amount: Math.round((i.net * percent) / 100) };
  });
  const total = items.reduce((s, i) => s + i.amount, 0);
  const bucketLabel = {
    current: t("Not yet due"),
    d30: t("1–30 days overdue"),
    d60: t("31–60 days overdue"),
    d90: t("61–90 days overdue"),
    d180: t("91–180 days overdue"),
    d180plus: t("More than 180 days overdue"),
    handed_over: t("Handed over to collection"),
    insolvent: t("Insolvent")
  };

  const book = async () => {
    setBooking(true);
    try {
      const res = await api.postAllowance({ year, overrides });
      message.success(
        res.data.entry
          ? `${t("Booked")}: ${t("journal entry")} ${res.data.entry.entryNo}`
          : t("Nothing to book: the allowance is already up to date.")
      );
      setData(res.data.proposal);
      setOverrides({});
      if (onBooked) onBooked();
    } catch (error) {
      message.error(errorText(error));
    } finally {
      setBooking(false);
    }
  };

  return (
    <Card
      size="small"
      style={{ marginTop: 16 }}
      title={t("Bad debt allowance (specific and general)")}
      extra={
        <Popconfirm
          title={t("Book the allowance on 31.12. and reverse it on 01.01. of the next year?")}
          onConfirm={book}
          okText={t("Book")}
          cancelText={t("Cancel")}
        >
          <Button type="primary" loading={booking} disabled={!data.accounts?.complete}>
            {t("Book allowance")}
          </Button>
        </Popconfirm>
      }
    >
      {!data.accounts?.complete ? (
        <Alert
          type="warning"
          showIcon
          style={{ marginBottom: 8 }}
          message={t(
            "The chart of accounts has no accounts for the bad debt allowance yet. Ask your tax advisor or add them in the chart of accounts."
          )}
        />
      ) : null}
      <Space wrap style={{ marginBottom: 8 }} className="acct-wrap">
        <span>
          {t("Proposed")}: <Amount cents={total} strong />
        </span>
        <span>
          {t("Already booked")}: <Amount cents={data.booked?.total} />
        </span>
        <span>
          {t("Open receivables at year end")}: <Amount cents={data.totals?.open} />
        </span>
      </Space>
      <ResponsiveTable
        mobileLayout="table"
        className="acct-grid"
        size="small"
        loading={loading}
        pagination={false}
        rowKey="item"
        dataSource={items}
        columns={[
          { title: t("Document"), dataIndex: "item" },
          { title: t("Customer"), dataIndex: "customerName" },
          { title: t("Due"), dataIndex: "dueDate", render: formatDate },
          {
            title: t("Age"),
            dataIndex: "bucket",
            render: v => <Tag>{bucketLabel[v] || v}</Tag>
          },
          {
            title: t("Kind"),
            dataIndex: "kind",
            render: v =>
              v === "specific" ? (
                <Tag color="red">{t("Specific")}</Tag>
              ) : (
                <Tag color="blue">{t("General")}</Tag>
              )
          },
          {
            title: t("Net open"),
            dataIndex: "net",
            align: "end",
            render: v => <Amount cents={v} />
          },
          {
            title: "%",
            dataIndex: "percent",
            align: "end",
            render: (v, r) => (
              <InputNumber
                size="small"
                min={0}
                max={100}
                value={v}
                onChange={val =>
                  setOverrides(o => ({ ...o, [r.item]: val === null ? undefined : val }))
                }
                style={{ width: 72 }}
              />
            )
          },
          {
            title: t("Allowance"),
            dataIndex: "amount",
            align: "end",
            render: v => <Amount cents={v} strong />
          }
        ]}
      />
      <Typography.Paragraph type="secondary" style={{ marginTop: 8 }}>
        {t(
          "Based on the net open items at the year end. The allowance has no VAT effect; VAT is only corrected when a receivable is written off."
        )}
      </Typography.Paragraph>
    </Card>
  );
};

export default AllowanceSection;
